import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { CircleFlag } from 'react-circle-flags';

class CheckboxDropdownFilterActive extends Component {
    constructor(props) {
        super();

        this.toggleDropdown = this.toggleDropdown.bind(this);
        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.showName = this.showName.bind(this);
        this.selectAdditionalFilter = this.selectAdditionalFilter.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleSelectAllShown = this.handleSelectAllShown.bind(this);

        this.dropdownRef = React.createRef();
        this.state = {
            isOpen: false,
            selectedOptions: props.alreadySelected ?? [],
            allSelected: false,
            brandType: [],
            activeType: [],
            options: [...props.options],
            searchValue: '',
            searchedOptions: [...props.options],
            shownName: props.alreadySelected?.length
                ? props.t('ai_tracking.keyword_detail.prompts_select_header', {
                      selectedNumber: props.alreadySelected.length,
                      totalNumber: props.options.length,
                  })
                : props.label,
        };
    }

    handleClickOutside(event) {
        if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target)) {
            this.setState({
                isOpen: false,
            });
        }
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentDidUpdate(prevProps) {
        if (
            JSON.stringify(prevProps.values) !== JSON.stringify(this.props.values) &&
            JSON.stringify(prevProps.selectedTags) !== JSON.stringify(this.props.selectedTags)
        ) {
            this.setState({
                selectedOptions: this.props.values,
                allSelected: false,
                brandType: [],
                activeType: [],
                searchValue: '',
                options: [...this.props.options],
                searchedOptions: [...this.props.options],
            });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    toggleDropdown() {
        this.setState({
            isOpen: !this.state.isOpen,
        });
    }

    handleCheckboxChange(option) {
        // Selecting an individual option is not the same as "All": clear the
        // explicit all-selected flag so the "All" checkbox never auto-checks.
        this.setState({
            allSelected: false,
            selectedOptions: this.state.selectedOptions.includes(option)
                ? this.state.selectedOptions.filter((item) => item !== option)
                : [...this.state.selectedOptions, option],
        });
    }

    handleApply() {
        this.props.onApply(
            this.state.selectedOptions,
            this.state.selectedOptions.length === this.props.options.length,
        );
        this.toggleDropdown();
        this.showName();
    }

    showName() {
        if (this.state.selectedOptions.length) {
            this.setState({
                shownName: this.props.t('ai_tracking.keyword_detail.prompts_select_header', {
                    selectedNumber: this.state.selectedOptions.length,
                    totalNumber: this.props.options.length,
                }),
            });
        } else {
            this.setState({
                shownName: this.props.label,
            });
        }
    }

    selectAdditionalFilter(value, type) {
        if (value === 'all') {
            // "All" is an explicit, standalone state: toggling it selects/clears
            // every option and unselects the branded/active filters.
            const nextAllSelected = !this.state.allSelected;
            this.setState({
                allSelected: nextAllSelected,
                brandType: [],
                activeType: [],
                options: [...this.props.options],
                selectedOptions: nextAllSelected
                    ? this.props.options.map((item) => item.value)
                    : [],
                searchValue: '',
                searchedOptions: [...this.props.options],
            });
            return;
        }

        let brandType = this.state.brandType;
        let activeType = this.state.activeType;

        if (type === 'brand') {
            // branded and non-branded are mutually exclusive: clicking one replaces
            // the other, and clicking the active one again clears the brand filter.
            brandType = brandType.includes(value) ? [] : [value];
        }
        if (type === 'active') {
            // active and inactive are mutually exclusive: clicking one replaces the
            // other, and clicking the active one again clears the status filter.
            activeType = activeType.includes(value) ? [] : [value];
        }

        let options = this.props.options;
        let selectedOptions = [];

        if (brandType?.length === 1) {
            if (brandType.includes('branded')) {
                options = options.filter((item) =>
                    item.label?.toLowerCase()?.includes(this.props.brand?.toLowerCase()),
                );
            } else {
                options = options.filter(
                    (item) => !item.label?.toLowerCase()?.includes(this.props.brand?.toLowerCase()),
                );
            }
        }
        if (activeType?.length === 1) {
            if (activeType.includes('active')) {
                options = options.filter((item) => item.status === 'active');
            } else {
                options = options.filter((item) => item.status === 'inactive');
            }
        }

        if (brandType?.length === 0 && activeType?.length === 0) {
            selectedOptions = [];
        } else {
            selectedOptions = options.map((item) => item.value);
        }

        this.setState({
            // Any branded/active filter deselects "All".
            allSelected: false,
            brandType: brandType,
            activeType: activeType,
            options,
            selectedOptions,
            searchValue: '',
            searchedOptions: options,
        });
    }

    handleSearchChange(e) {
        this.setState({
            searchValue: e.target.value,
            searchedOptions: this.state.options.filter((item) =>
                item.label?.toLowerCase()?.includes(e.target.value.toLowerCase()),
            ),
        });
    }

    handleSelectAllShown() {
        // Bulk select/deselect every option currently visible in the list
        // (i.e. the search-filtered results). Toggles: if all shown options are
        // already selected, this clears them; otherwise it selects them all
        // while preserving selections outside the current search.
        const shownValues = this.state.searchedOptions.map((item) => item.value);
        const allShownSelected =
            shownValues.length > 0 &&
            shownValues.every((value) => this.state.selectedOptions.includes(value));

        this.setState({
            allSelected: false,
            selectedOptions: allShownSelected
                ? this.state.selectedOptions.filter((value) => !shownValues.includes(value))
                : [...new Set([...this.state.selectedOptions, ...shownValues])],
        });
    }

    render() {
        const options = this.state.options;
        const { t } = this.props;
        const shownValues = this.state.searchedOptions.map((item) => item.value);
        const allShownSelected =
            shownValues.length > 0 &&
            shownValues.every((value) => this.state.selectedOptions.includes(value));

        return (
            <div className="checkbox-dropdown" ref={this.dropdownRef}>
                <button onClick={this.toggleDropdown} className="dropdown-btn">
                    <span className="checkbox-dropdown-text">{this.state.shownName}</span>
                    <div className="dropdown-arrow">
                        <i className="icon-arrow-down"></i>
                    </div>
                </button>
                {this.state.isOpen && (
                    <div className="dropdown-content">
                        <div>
                            <div className="additional-filter">
                                <label className="checkbox-dropdown-option" key={'branded'}>
                                    <input
                                        type="checkbox"
                                        value={'all'}
                                        checked={this.state.allSelected}
                                        onChange={() => this.selectAdditionalFilter('all')}
                                    />
                                    {t('ai_tracking.keyword_detail.prompts_select.all')}
                                </label>
                            </div>
                            <div className="additional-filter">
                                <div>
                                    <label className="checkbox-dropdown-option" key={'branded'}>
                                        <input
                                            type="checkbox"
                                            value={'branded'}
                                            checked={this.state.brandType.includes('branded')}
                                            onChange={() =>
                                                this.selectAdditionalFilter('branded', 'brand')
                                            }
                                        />
                                        {t('ai_tracking.keyword_detail.prompts_select.branded')}
                                    </label>
                                </div>
                                <div>
                                    <label className="checkbox-dropdown-option" key={'non-branded'}>
                                        <input
                                            type="checkbox"
                                            value={'non-branded'}
                                            checked={this.state.brandType.includes('non-branded')}
                                            onChange={() =>
                                                this.selectAdditionalFilter('non-branded', 'brand')
                                            }
                                        />
                                        {t('ai_tracking.keyword_detail.prompts_select.nonBranded')}
                                    </label>
                                </div>
                            </div>
                            <div className="additional-filter">
                                <div>
                                    <label className="checkbox-dropdown-option" key={'branded'}>
                                        <input
                                            type="checkbox"
                                            value={'active'}
                                            checked={this.state.activeType.includes('active')}
                                            onChange={() =>
                                                this.selectAdditionalFilter('active', 'active')
                                            }
                                        />
                                        {t('ai_tracking.keyword_detail.prompts_select.active')}
                                    </label>
                                </div>
                                <div>
                                    <label className="checkbox-dropdown-option" key={'non-branded'}>
                                        <input
                                            type="checkbox"
                                            value={'inactive'}
                                            checked={this.state.activeType.includes('inactive')}
                                            onChange={() =>
                                                this.selectAdditionalFilter('inactive', 'active')
                                            }
                                        />
                                        {t('ai_tracking.keyword_detail.prompts_select.inactive')}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div>
                            <input
                                className="form-control mt-10"
                                value={this.state.searchValue}
                                onChange={this.handleSearchChange}
                                placeholder="Search..."
                            />
                        </div>
                        <div className="dropdown-shown-bar">
                            <label className="checkbox-dropdown-option">
                                <input
                                    type="checkbox"
                                    checked={allShownSelected}
                                    disabled={!shownValues.length}
                                    onChange={this.handleSelectAllShown}
                                />
                                {t('ai_tracking.keyword_detail.prompts_select.select_all_shown')}
                            </label>
                            <span className="dropdown-shown-count">
                                {t('ai_tracking.keyword_detail.prompts_select.shown_count', {
                                    count: shownValues.length,
                                })}
                            </span>
                        </div>
                        <div className="dropdown-content-body">
                            {this.state.searchedOptions.map((option, index) => (
                                <label className="checkbox-dropdown-option" key={index}>
                                    <input
                                        type="checkbox"
                                        value={option.value}
                                        checked={this.state.selectedOptions.includes(option.value)}
                                        onChange={() => this.handleCheckboxChange(option.value)}
                                    />
                                    {option.country && (
                                        <CircleFlag
                                            countryCode={option.country.toLowerCase()}
                                            height="22"
                                            width="22"
                                            style={{ marginRight: '8px' }}
                                        />
                                    )}
                                    {option?.showedValue ?? option.label}
                                </label>
                            ))}
                            {!options?.length && (
                                <div className="no-options">No options available</div>
                            )}
                        </div>
                        <button onClick={this.handleApply} className="apply-btn">
                            {t('ai_tracking.keyword_detail.prompts_select.apply', {
                                selectedNumber: this.state.selectedOptions.length,
                                totalNumber: this.props.options.length,
                            })}
                        </button>
                    </div>
                )}
            </div>
        );
    }
}

export default withTranslation()(CheckboxDropdownFilterActive);
